import React, {useState} from 'react';
import {Box, Button, Divider, Paper, styled} from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {generateUUID} from '../../../../utility/uuidUtil';
import PropTypes from 'prop-types';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {getComponentByType} from './FormBuilderComponents';
import {FORM_BUILDER_COMPONENTS} from './ComponentList';
import ComponentBuilder from './Components/ComponentBuilder';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

const DeleteComponentIcon = styled(DeleteIcon)(({theme}) => ({
    color: theme.palette.primary.main, cursor: 'pointer'
}));

const FormBuilder = ({schema, setSchema, submitMode = false, handleSubmit, data, readOnly, isFormLive}) => {
    const [previewMode, setPreviewMode] = useState(false);
    const [submitData, setSubmitData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    const handleChangeEdit = ({
                                  index,
                                  description,
                                  validation,
                                  enableValidation,
                                  option,
                                  optionId,
                                  isAdd,
                                  isRemove,
                                  files
                              }) => {
        const newForm = JSON.parse(JSON.stringify(schema));
        if (description !== undefined) {
            newForm[index].description = description;
        }
        if (validation !== undefined) {
            newForm[index].validation = validation;
        }
        if (enableValidation !== undefined) {
            newForm[index].enableValidation = enableValidation;
        }
        if (option !== undefined) {
            let opt = newForm[index].options.find(e => e.index === optionId);
            opt.description = option
        }
        if (isAdd) {
            newForm[index].options = [...newForm[index].options, {
                index: newForm[index].options.reduce((max, obj) => Math.max(max, obj.index), 0) + 1,
                description: ''
            }]
        }
        if (isRemove) {
            newForm[index].options = newForm[index].options.filter(e => e.index !== optionId)
        }
        if (files !== undefined) {
            newForm[index].attachments = files;
        }
        setSchema(newForm);
    };

    const handleChangeSubmit = ({id, value, optionIndex, optionValue, radioValue}) => {
        const newSubmitData = {...submitData};
        if (value !== undefined) {
            newSubmitData[id] = value;
        } else if (optionIndex !== undefined && optionValue !== undefined) {
            if (!newSubmitData[id]) {
                newSubmitData[id] = [];
            }
            newSubmitData[id] = optionValue ? [...newSubmitData[id], optionIndex] : newSubmitData[id].filter(o => o !== optionIndex)
            if (newSubmitData[id].length === 0) {
                newSubmitData[id] = undefined;
            }
        } else if (radioValue !== undefined) {
            newSubmitData[id] = radioValue;
        }
        validate(id, value)
        setSubmitData(newSubmitData);
    };

    const validate = (id, value) => {
        const field = schema.find((e) => e.id === id);
        if (field?.validation?.min > 0) {
            const newValidationErrors = {...validationErrors};
            newValidationErrors[id] = value.length < field.validation.min
                ? `Text must be at least ${field.validation.min} characters long`
                : undefined;
            setValidationErrors(newValidationErrors)
        }
    }

    const removeComponent = (position) => {
        setSchema(schema.filter((e, index) => index !== position))
    }

    const moveComponent = (position, swapPosition) => {
        let newArray = [...schema];
        let temp = newArray[position];
        newArray[position] = newArray[swapPosition];
        newArray[swapPosition] = temp;
        setSchema(newArray);
    }

    const onDragEnd = result => {
        if (!result.destination) {
            return;
        }

        moveComponent(result.source.index, result.destination.index);
    };

    const addComponent = (type) => setSchema([...schema, {
        ...getComponentByType(type),
        enableValidation: type === 'signature',
        id: generateUUID()
    }]);

    const buildEditRows = (f, index) =>
        <>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} py={1}>
                <Typography>{index + 1}. {f.label}</Typography>
                <Box display={'flex'} gap={1}>
                    {!isFormLive && <DeleteComponentIcon onClick={() => removeComponent(index)}/>}

                    {index !== 0 && !isFormLive && <ArrowUpwardOutlinedIcon onClick={() =>
                        moveComponent(index, index - 1)} sx={{cursor: 'pointer'}}/>}
                    {index !== schema.length - 1 && !isFormLive &&
                        <ArrowDownwardOutlinedIcon onClick={() =>
                            moveComponent(index, index + 1)} sx={{cursor: 'pointer'}}/>}
                </Box>

            </Box>
        </>

    return (
        <>
            <Box display={'flex'} justifyContent={'end'}>{!submitMode &&
                <Button onClick={() => setPreviewMode(!previewMode)} sx={{width: '100px'}}>
                    {previewMode ? 'Edit' : 'Preview'}
                </Button>}
            </Box>
            <Box display={'flex'} fullWidth justifyContent={'center'}>
                <Paper variant={'outlined'}
                       sx={{flex: 2, p: 2, mx: submitMode ? 0 : '20%', mb: 2, mt: submitMode ? 2 : 0}}
                       mx={previewMode ? '20%' : 0}>
                    {!submitMode && !previewMode
                        ? <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided) => (
                                    <Box
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {schema?.map((f, index) =>
                                            <Draggable key={index} draggableId={index.toString()} index={index} isDragDisabled={isFormLive}>
                                                {(provided) => (
                                                    <Box
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <Paper sx={{m: '0 20px 10px 20px', p: 3}} variant="outlined"
                                                        >
                                                            {buildEditRows(f, index)}
                                                            <ComponentBuilder handleChangeSubmit={handleChangeSubmit}
                                                                              index={index} readOnly={readOnly}
                                                                              handleChangeEdit={handleChangeEdit}
                                                                              previewMode={previewMode} field={f}
                                                                              data={data} submitMode={submitMode}
                                                                              submitData={submitData}
                                                                              isFormLive={isFormLive}
                                                                              validationErrors={validationErrors}/>
                                                        </Paper>
                                                    </Box>
                                                )}
                                            </Draggable>
                                        )}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </DragDropContext>
                        : schema?.map((f, index) =>
                            <Box key={`formItem-${index}`} px={2} py={1}>
                                <ComponentBuilder handleChangeSubmit={handleChangeSubmit}
                                                  index={index} readOnly={readOnly}
                                                  handleChangeEdit={handleChangeEdit}
                                                  previewMode={previewMode} field={f}
                                                  data={data} submitMode={submitMode}
                                                  submitData={submitData} isFormLive={isFormLive}
                                                  validationErrors={validationErrors}/>
                            </Box>
                        )
                    }
                    {!readOnly && !previewMode && <Divider/>}
                    <Box display={'flex'} alignItems={'center'} justifyContent={submitMode ? 'end' : 'start'} gap={1}
                         flexWrap={'wrap'} pt={1}>
                        {!submitMode && !previewMode
                            ? FORM_BUILDER_COMPONENTS.map((component, index) =>
                                <Button disabled={isFormLive} key={`${component.type}-${index}`} variant={'outlined'}
                                        onClick={() => addComponent(component.type)}>{component.description}</Button>)
                            : readOnly || previewMode
                                ? null
                                : <Button onClick={() => handleSubmit(submitData, {})}
                                          disabled={schema.some(field => field.enableValidation &&
                                              (!submitData[field.id] || validationErrors[field.id]))}>
                                    Submit
                                </Button>
                        }
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

FormBuilder.propTypes = {
    schema: PropTypes.array,
    setSchema: PropTypes.func,
    submitMode: PropTypes.bool,
    handleSubmit: PropTypes.func,
    data: PropTypes.object,
    readOnly: PropTypes.bool,
    isFormLive: PropTypes.bool
};

export default FormBuilder;